import { useGraphqlQuery } from "graphql-mock"
import { useHasValueChanged } from "hooks/src/useHasValueChanged"
import { Card, ErrorFallback, Icon } from "materia"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  BASE_PALETTE,
  generatePalette,
  getAccessibleTextColorForBackgroundColor,
  modifiers,
  type Palette,
} from "ui-tools"
import { getIsVideoUrl } from "../../dataUtilities/getIsVideoUrl"
import { useCurrentCompanyContext } from "../AppShell/CurrentCompanyProvider"
import { CheckpointCard, type CheckpointProduct } from "./CheckpointCard"
import { CheckpointUnlockedModal } from "./CheckpointUnlockedModal"
import { getIslandColor } from "./getIslandColor"
import { query } from "./gql"

import s from "./CheckpointIsland.module.scss"

const CHECKPOINT_ISLAND_ID = "checkpoint_island"

export const CheckpointIslandConnected = ({
  journeyId,
}: {
  journeyId: string
}) => {
  const { data, error, isPending } = useGraphqlQuery(query, {
    id: journeyId,
  })

  const { isLocked, mainJourneyProgress, productCategorizations } =
    data?.productCategory ?? {}
  const { certifiedCount = 0, certifiedMax = 0 } = mainJourneyProgress ?? {}

  const isCheckpointIslandLocked = isLocked || certifiedCount < certifiedMax
  const products = (productCategorizations?.nodes ?? []).reduce(
    (acc: CheckpointProduct[], node) => {
      if (node.product !== null) acc.push(node.product)
      return acc
    },
    []
  )

  const {
    backgroundImageUrl,
    teaserImageUrl,
    checkpointIslandBackgroundColor,
  } = data?.productCategory ?? {}

  const bgImg = getIsVideoUrl(backgroundImageUrl ?? "")
    ? teaserImageUrl
    : backgroundImageUrl

  const palette = usePalette(checkpointIslandBackgroundColor, bgImg)

  if (!isPending && products.length === 0) return null

  return (
    <CheckpointIsland
      key={journeyId}
      isLoading={isPending}
      error={error}
      products={products}
      isLocked={isCheckpointIslandLocked}
      palette={palette}
    />
  )
}

type CheckpointIslandProps = {
  isLoading: boolean
  error: unknown
  isLocked: boolean
  palette: Palette
  products: CheckpointProduct[]
}
export const CheckpointIsland = ({
  isLoading,
  error,
  isLocked,
  products,
  palette,
}: CheckpointIslandProps) => {
  const { t } = useTranslation()

  const [didUnlockModalClose, setDidUnlockModalClose] = useState(false)

  const isLockedChanged = useHasValueChanged(isLocked)

  const shouldPlayUnlockAnimation =
    !isLocked && isLockedChanged && didUnlockModalClose

  const onModalClose = () => {
    setDidUnlockModalClose(true)
    const checkpointElement = document.getElementById(CHECKPOINT_ISLAND_ID)
    if (checkpointElement)
      checkpointElement.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  const getContent = () => {
    if (error) {
      return (
        <ErrorFallback
          error={error}
          title={t("error.GENERIC")}
          message={t("error.CHECKPOINT_FETCH_ERROR")}
        />
      )
    }

    return (
      <>
        <div className={s.checkpointIsland__cards} id={CHECKPOINT_ISLAND_ID}>
          {products.map((product) => (
            <CheckpointCard
              key={product.id}
              product={product}
              isLocked={isLocked}
              palette={palette}
              shouldPlayUnlockAnimation={shouldPlayUnlockAnimation}
            />
          ))}
        </div>

        <LockedOverlay
          isLocked={isLocked}
          palette={palette}
          shouldPlayUnlockAnimation={shouldPlayUnlockAnimation}
        />

        <CheckpointUnlockedModal
          isUnlocked={!isLocked}
          onModalClose={onModalClose}
        />
      </>
    )
  }

  // No loading state for something that might not exist
  if (isLoading) return null

  return (
    <Card backgroundColor={palette.primary} borderRadius="large">
      <div className={s.checkpointIsland}>{getContent()}</div>
    </Card>
  )
}

const LockedOverlay = ({
  isLocked,
  palette,
  shouldPlayUnlockAnimation,
}: {
  isLocked: boolean
  palette: Palette
  shouldPlayUnlockAnimation: boolean
}) => {
  const { t } = useTranslation()

  const [isVisible, setIsVisible] = useState(isLocked)

  const messageStyle = {
    backgroundColor: palette.secondary,
    color: getAccessibleTextColorForBackgroundColor(palette.secondary),
  }

  return (
    <div
      className={modifiers(s, "lockedOverlay", {
        isVisible,
        enableAnimations: shouldPlayUnlockAnimation,
      })}
    >
      <div
        className={s.lockedOverlay__glass}
        onAnimationEnd={() => setIsVisible(false)}
      />

      <div className={s.lockedOverlay__message} style={messageStyle}>
        <Icon icon="Lock" size="medium" />
        {t("journey.CHECKPOINT_LOCKED")}
      </div>
    </div>
  )
}

const usePalette = (
  storedColor: string | null | undefined,
  imageSrc: string | null | undefined
) => {
  const {
    currentCompany: { companyStarColor },
  } = useCurrentCompanyContext()

  const { data: palette = BASE_PALETTE } = useQuery({
    queryKey: [
      "checkpointIsland",
      "color",
      storedColor,
      imageSrc,
      companyStarColor,
    ],
    queryFn: () => getIslandColor(storedColor, imageSrc, companyStarColor),
    select: generatePalette,
    staleTime: Number.POSITIVE_INFINITY,
  })

  return palette
}
